import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

import { getNewsList } from 'context/actions/news';
import PageHeader from 'components/PageHeader';

const PAGE_LIMIT = 12;

export default function NewsListPage() {
    const { t } = useTranslation();
    const router = useRouter();
    const lang = useSelector((s) => s.system.lang || 'mn');

    const [newsList, setNewsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total_page: 1, total_count: 0 });
    const [search, setSearch] = useState('');

    // URL query-аас page унших
    useEffect(() => {
        const p = parseInt(router.query.page, 10);
        if (p && p > 0) setPage(p);
        if (router.query.search) setSearch(String(router.query.search));
    }, [router.query]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await getNewsList(search, page, PAGE_LIMIT, 'news');
            if (cancelled) return;
            if (res?.data?.list) {
                setNewsList(res.data.list);
                setMeta(res.data.meta || { total_page: 1, total_count: 0 });
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [page, search]);

    const changePage = (newPage) => {
        router.push(
            { pathname: '/news', query: { ...router.query, page: newPage } },
            undefined,
            { shallow: false }
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const title = lang === 'en' ? 'Latest News' : 'Цаг үеийн мэдээ';

    return (
        <>
            <Head>
                <title>{`${title} — Mongol Refinery`}</title>
            </Head>

            {/* ─── Page hero ─── */}
            <PageHeader slug="news" title={title} />

            {/* ─── News grid ─── */}
            <section className="py-16 lg:py-20 bg-brand-cream">
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-white animate-pulse"
                                >
                                    <div className="aspect-[4/3] bg-brand-cream-dark" />
                                    <div className="p-5 space-y-3">
                                        <div className="h-3 bg-brand-cream-dark w-1/3" />
                                        <div className="h-4 bg-brand-cream-dark w-full" />
                                        <div className="h-4 bg-brand-cream-dark w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : newsList.length === 0 ? (
                        <div className="text-center py-20 text-brand-gray">
                            {t('news.noNewsFound')}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {newsList.map((item) => (
                                    <NewsCard key={item.id} item={item} lang={lang} t={t} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {meta.total_page > 1 && (
                                <Pagination
                                    page={page}
                                    totalPage={meta.total_page}
                                    onChange={changePage}
                                />
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
}

function NewsCard({ item, lang, t }) {
    const title = lang === 'en' && item.nameEn ? item.nameEn : item.name;
    const description = lang === 'en' && item.descriptionEn ? item.descriptionEn : item.description;
    const imageUrl = item.imageUrl || '/placeholders/news-default.svg';

    return (
        <Link
            href={`/news/${item.id}`}
            className="group flex flex-col bg-white overflow-hidden hover:shadow-xl transition-all"
        >
            <div className="relative aspect-[16/10] bg-brand-dark overflow-hidden">
                <Image
                    src={imageUrl}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {item.isSpecial && (
                    <div className="absolute top-3 left-3 bg-brand-yellow text-brand-dark px-2 py-1 text-[10px] font-black uppercase tracking-wider">
                        {lang === 'en' ? 'Featured' : 'Онцлох'}
                    </div>
                )}
            </div>
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-brand-gray mb-3">
                    <Calendar size={12} className="text-brand-yellow-dark" />
                    {item.createdAtDay}
                </div>
                <h3 className="text-lg font-bold text-brand-dark leading-snug line-clamp-2 mb-3 group-hover:text-brand-yellow-dark transition">
                    {title}
                </h3>
                {description && (
                    <p className="text-sm text-brand-gray line-clamp-2 leading-relaxed">
                        {description.replace(/<[^>]+>/g, '')}
                    </p>
                )}
            </div>
        </Link>
    );
}

function Pagination({ page, totalPage, onChange }) {
    const pages = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPage, start + 4);

    for (let i = start; i <= end; i++) pages.push(i);

    return (
        <div className="mt-12 flex items-center justify-center gap-2">
            <button
                onClick={() => onChange(page - 1)}
                disabled={page === 1}
                className="w-10 h-10 flex items-center justify-center border border-brand-cream-dark hover:border-brand-yellow disabled:opacity-30 disabled:cursor-not-allowed transition"
                aria-label="Previous"
            >
                <ChevronLeft size={18} />
            </button>
            {pages.map((p) => (
                <button
                    key={p}
                    onClick={() => onChange(p)}
                    className={`w-10 h-10 flex items-center justify-center font-bold transition ${
                        p === page
                            ? 'bg-brand-dark text-white'
                            : 'border border-brand-cream-dark hover:border-brand-yellow text-brand-dark'
                    }`}
                >
                    {p}
                </button>
            ))}
            <button
                onClick={() => onChange(page + 1)}
                disabled={page === totalPage}
                className="w-10 h-10 flex items-center justify-center border border-brand-cream-dark hover:border-brand-yellow disabled:opacity-30 disabled:cursor-not-allowed transition"
                aria-label="Next"
            >
                <ChevronRight size={18} />
            </button>
        </div>
    );
}